import { useState, useEffect } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import portalService from '../../services/portalService';
import { formatCurrency } from '../../utils/amountUtils';
import { formatDate as formatDateSafe } from '../../utils/dateUtils';
import StatusBadge from '../shared/StatusBadge';

function PortalHome() {
  const { portalData } = useOutletContext();
  const { publicId } = useParams();
  const [summary, setSummary] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');
  const [periodBalances, setPeriodBalances] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  
  const periodOptions = [
    { value: 'this_month', label: 'This Month', icon: '📅' },
    { value: 'last_month', label: 'Last Month', icon: '📆' },
    { value: 'last_quarter', label: 'Last Quarter', icon: '📊' },
    { value: 'last_year', label: 'Last Year', icon: '📈' },
    { value: 'all_time', label: 'All Time', icon: '🗓️' }
  ];
  
  // Use token or publicId as the identifier
  const identifier = portalData?.customerPublicId || publicId;
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!identifier) return;
      
      try {
        setLoading(true);
        
        // Fetch customer summary
        const customerData = await portalService.getCustomerInfo(identifier);
        setSummary(customerData.summary);
        console.log('Customer summary data:', customerData.summary);
        
        // Fetch recent invoices
        const invoiceData = await portalService.getInvoices(identifier, { limit: 5 });
        setRecentInvoices(invoiceData.invoices || []);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [identifier]);
  
  useEffect(() => {
    const fetchPeriodBalances = async () => {
      if (!identifier) return;
      
      try {
        setLoadingPeriod(true);
        // Always fetch from the balances endpoint for consistency
        const balanceData = await portalService.getBalances(identifier, selectedPeriod);
        console.log(`Balance data for ${selectedPeriod}:`, balanceData);
        setPeriodBalances(balanceData.balances);
      } catch (error) {
        console.error('Error fetching period balances:', error);
        setPeriodBalances({ periodPaid: 0 });
      } finally {
        setLoadingPeriod(false);
      }
    };
    
    fetchPeriodBalances();
  }, [identifier, selectedPeriod]);
  
  // formatCurrency utility now imported from amountUtils
  
  // Thin wrapper around shared timezone-safe formatter — uses short-month form
  // for the home dashboard (other portal screens use long).
  const formatDate = (date) => formatDateSafe(date, { year: 'numeric', month: 'short', day: 'numeric' });
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Current Balance Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Current Balance</h3>
            <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(summary?.outstandingBalance || 0)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Outstanding amount
          </p>
        </div>

        {/* Available Credit Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Available Credit</h3>
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className={`text-2xl font-bold ${summary?.availableCredit > 0 ? 'text-green-600' : 'text-gray-400'}`}>
            {formatCurrency(summary?.availableCredit || 0)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Credit on account
          </p>
        </div>

        {/* Open Invoices Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Open Invoices</h3>
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {summary?.openInvoices || 0}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Awaiting payment
          </p>
        </div>
        
        {/* Payments Card with Period Selector */}
          <div className="bg-white rounded-lg shadow p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Payments</h3>
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <p className="text-2xl font-bold text-gray-900 mb-8">
              {loadingPeriod ? (
                <span className="text-gray-400">...</span>
              ) : (
                formatCurrency(periodBalances?.periodPaid || 0)
              )}
            </p>
            
            <div className="absolute bottom-4 right-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="text-sm text-gray-500 bg-transparent border-0 focus:ring-0 cursor-pointer hover:text-gray-700"
              >
                {periodOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
      </div>
      
      {/* Recent Invoices */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Recent Invoices</h3>
          <Link
            to="invoices"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
          >
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="divide-y divide-gray-200">
          {recentInvoices.length > 0 ? (
            recentInvoices.map((invoice) => (
              <div key={invoice.id} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Invoice #{invoice.invoiceNumber}
                    </p>
                    <p className="text-sm text-gray-500">
                      Due {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(invoice.totalAmount)}
                    </p>
                    <StatusBadge 
                      type="invoice_status" 
                      value={{
                        status: invoice.status,
                        paymentStatus: invoice.paymentStatus,
                        dueDate: invoice.dueDate,
                        displayStatus: invoice.displayStatus
                      }}
                      size="xs"
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2">No invoices found</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="invoices"
            className="bg-white rounded-lg p-4 hover:shadow-md transition-all hover:-translate-y-0.5 flex items-center group"
          >
            <div className="bg-blue-100 rounded-lg p-3 mr-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-900">View All Invoices</p>
              <p className="text-sm text-gray-500">
                {summary?.openInvoices > 0 
                  ? `${summary.openInvoices} invoices need attention`
                  : 'All invoices are up to date'}
              </p>
            </div>
          </Link>
          
          <Link
            to="payment-history"
            className="bg-white rounded-lg p-4 hover:shadow-md transition-all hover:-translate-y-0.5 flex items-center group"
          >
            <div className="bg-green-100 rounded-lg p-3 mr-4 group-hover:bg-green-200 transition-colors">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-900">Payment History</p>
              <p className="text-sm text-gray-500">View your payment records</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PortalHome;